import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  DiscussionTab,
  ChatMember,
  ChatMessage,
  ChatAttachment,
  Discussion,
  ReadReceipt,
} from "../types/messaging";
import type { User } from "../types/user";
import { messagerieApi } from "../services/api/messagerie";
import { showNotification } from "context/notification.service";
import { compressImages } from "../utils/compressImage";
import { matchesNamePrefix, includesNormalized } from "../utils/textSearch";
import { useAuthStore } from "./auth";
import { useDataStore } from "./user_org";
import { useApiConfigStore } from "./api_config";
import { useInAppNotificationsStore } from "./inAppNotificationsStore";
import { useNotificationPreferencesStore } from "./notificationPreferencesStore";
import firebase from "@services/firebase";
import { collection, query, where, orderBy, limit as fsLimit, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp, updateDoc } from "firebase/firestore";

// --- Helpers ---

/** Discussions réunies sous l'onglet « Interne » : le fil d'équipe et la
 *  conversation privée sont deux façons de se parler entre collègues, les
 *  chercher dans deux onglets séparés n'avait pas de sens. Rien ne change en
 *  base : le `tab` d'une discussion reste "generales" ou "privees". */
export const INTERNE_TABS: DiscussionTab[] = ["generales", "privees"];

/** Ramène un onglet stocké/mémorisé sur celui réellement affiché. */
export function normalizeTab(tab: DiscussionTab): DiscussionTab {
  return INTERNE_TABS.includes(tab) ? "interne" : tab;
}

/** La discussion appartient-elle à l'onglet affiché ? */
export function tabMatches(discussionTab: DiscussionTab, activeTab: DiscussionTab): boolean {
  if (activeTab === "interne") return INTERNE_TABS.includes(discussionTab);
  return discussionTab === activeTab;
}

/** Cette discussion a-t-elle des messages non lus ?
 *  Le fil interne d'un ordre n'est pas listé dans la barre latérale (on y accède
 *  par les onglets du ChatPanel) : ses non-lus doivent donc remonter sur la
 *  discussion client du même ordre/dossier, comme pour la pastille d'onglet. */
export function discussionHasUnread(
  discussion: Discussion,
  discussions: Discussion[],
  unreadCounts: Record<string, number>,
): boolean {
  if ((unreadCounts[discussion.id] || 0) > 0) return true;
  const entityId = discussion.ordre_id || discussion.dossier_id;
  if (!entityId) return false;
  const entityKey = discussion.ordre_id ? "ordre_id" : "dossier_id";
  const sibling = discussions.find((s) =>
    s.type === "internal" && s.id !== discussion.id &&
    (entityKey === "ordre_id" ? s.ordre_id === entityId : s.dossier_id === entityId)
  );
  return !!sibling && (unreadCounts[sibling.id] || 0) > 0;
}

/** Horodatage brut du dernier message (ISO), vide si le fil n'en a aucun.
 *  `lastMessageTime` est déjà formaté pour l'affichage (« 14:30 Hier ») et ne
 *  se trie pas. `updated_at` n'est pas un repli : il bouge sans message (fil
 *  créé par la synchro, membres modifiés…) et faisait passer des conversations
 *  vides devant des conversations actives. */
export function lastMessageAt(d: Discussion): string {
  return d.last_message?.timestamp || "";
}

/** Tri de la barre latérale : les fils avec messages d'abord, du plus récent au
 *  plus ancien ; les fils vides ensuite, par date de création. */
export function compareByLastMessage(a: Discussion, b: Discussion): number {
  const ma = lastMessageAt(a);
  const mb = lastMessageAt(b);
  if (ma && mb) return mb.localeCompare(ma);
  if (ma || mb) return ma ? -1 : 1;
  return (b.created_at || "").localeCompare(a.created_at || "");
}

/** Onglets réellement affichés dans la barre latérale. */
export const ALL_TABS: DiscussionTab[] = ["interne", "ordres"];

/** Dernier numéro de chargement par onglet (anti réponse périmée). */
const _discFetchSeq: Record<string, number> = {};

/** Fils en cours de chargement ciblé : React peut monter deux fois le panneau. */
const inflightDiscussionLoads = new Set<string>();

/** Nombre de discussions déjà ramenées par la pagination, par onglet. Compter
 *  les discussions en mémoire fausserait l'offset : certaines sont injectées
 *  hors pagination (non-lus hors page, lien profond). */
const _discLoaded: Record<string, number> = {};

const PRESENCE_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60 seconds

/** GET /messagerie/unread-counts : pas plus d'un appel toutes les 5 min, et jamais
 *  onglet masqué. Entre deux, le flux user_notifications tient les compteurs à jour
 *  (voir inAppNotificationsStore) ; l'appel ne sert qu'à recaler. */
const UNREAD_COUNTS_MIN_INTERVAL_MS = 5 * 60 * 1000;
let _lastUnreadCountsFetch = 0;

/** Écoute des discussions : seules celles dont l'utilisateur est membre et qui
 *  bougent depuis la veille. Toute activité nouvelle remet updated_at à
 *  maintenant, donc fait entrer la discussion dans l'écoute ; sans cette borne,
 *  chaque démarrage relisait toutes les discussions de l'org (4 800+). La marge
 *  d'un jour absorbe un décalage d'horloge du poste. */
const DISCUSSIONS_LISTEN_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Check if a user is online based on last_active_time freshness */
function isRecentlyActive(lastActiveTime: any): boolean {
  if (!lastActiveTime) return false;
  try {
    const ts = lastActiveTime.seconds
      ? lastActiveTime.seconds * 1000
      : new Date(lastActiveTime).getTime();
    return Date.now() - ts < PRESENCE_TIMEOUT_MS;
  } catch {
    return false;
  }
}

/** Map a backend User object to a lightweight ChatMember for the UI */
function userToChatMember(user: User): ChatMember {
  return {
    id: user.id,
    name: user.infos?.display_name || user.infos?.full_name || "Inconnu",
    role: user.role || "",
    online: isRecentlyActive(user.activity?.last_active_time),
    photo_url: user.customization?.photo_url || undefined,
    avatar_seed: user.customization?.avatar_seed || undefined,
  };
}

/** Format an ISO timestamp to a readable string like "14:30" or "14:30 Hier" */
function formatTimestamp(iso: string | undefined): string {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    const now = new Date();
    const time = date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isToday) return time;
    if (isYesterday) return `${time} Hier`;
    return `${time} ${date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}`;
  } catch {
    return iso;
  }
}

/** Normalise a backend ChatMessage so legacy camelCase fields are populated */
// Per-discussion debounce for the attachment-URL resolve fetch.
const _resolveTimers: Record<string, ReturnType<typeof setTimeout>> = {};

function normalizeMessage(msg: ChatMessage): ChatMessage {
  const rawTimestamp = msg.created_at || msg.timestamp;
  return {
    ...msg,
    senderId: msg.sender_id || msg.senderId,
    timestamp: formatTimestamp(rawTimestamp),
    replyToId: msg.reply_to_id || msg.replyToId,
    reactions: (msg.reactions || []).map((r) => ({
      ...r,
      userId: r.user_id || r.userId,
    })),
  };
}

/** Normalise a backend Discussion so legacy fields are populated */
function normalizeDiscussion(d: Discussion): Discussion {
  const lm = d.last_message;
  return {
    ...d,
    lastMessage:
      d.lastMessage ??
      (lm
        ? lm.attachment_type
          ? lm.attachment_type === "image"
            ? "Image"
            : lm.attachment_type === "audio"
              ? "Audio"
              : "Fichier"
          : lm.text || ""
        : ""),
    lastMessageTime:
      d.lastMessageTime ?? formatTimestamp(lm?.timestamp),
    unread: false, // unreadCounts store is the sole source of truth for unread state
  };
}

/** Build a sidebar preview string from a (non-deleted) message. */
function previewFromMessage(msg: ChatMessage | undefined): { text: string; time: string } {
  if (!msg) return { text: "", time: "" };
  const att = msg.attachment || msg.attachments?.[0];
  let text: string;
  if (att) {
    const isAudio = (att.mime_type || att.mimeType || "").startsWith("audio/");
    text = att.type === "image" ? "Image" : isAudio ? "Audio" : att.name || "Fichier";
  } else {
    const t = (msg.text || "").trim();
    text = t.substring(0, 40) + (t.length > 40 ? ".." : "");
  }
  const time = msg.timestamp || formatTimestamp(msg.created_at || msg.updated_at);
  return { text, time };
}

// --- Store ---

interface MessagerieStore {
  // State
  currentUserId: string;
  members: ChatMember[];
  discussions: Discussion[];
  messages: Record<string, ChatMessage[]>;
  activeTab: DiscussionTab;
  selectedDiscussionId: string | null;
  viewingDiscussionId: string | null; // the discussion actually visible (ChatPanel active tab)
  searchQuery: string;
  replyingToMessageId: string | null;
  unreadCounts: Record<string, number>;
  readReceipts: Record<string, ReadReceipt[]>;
  /** Transient: a message the UI should scroll/jump to (cleared after handling). */
  pendingScrollToMessageId: string | null;

  // Loading / connection
  isLoadingDiscussions: boolean;
  isLoadingMoreDiscussions: boolean;
  hasMoreDiscussions: boolean;
  isLoadingMessages: boolean;
  isLoadingOlderMessages: boolean;
  hasMoreMessages: Record<string, boolean>;
  isSending: boolean;
  uploadProgress: Record<string, number>; // optimisticId -> 0-100
  wsConnected: boolean;
  typingUsers: Record<string, string[]>; // discussionId -> userId[]
  _initialized: boolean;
  _unsubDiscussions: (() => void) | null;
  _unsubNotifications: (() => void) | null;
  _unsubPresence: (() => void) | null;
  _heartbeatInterval: ReturnType<typeof setInterval> | null;

  // Actions — async
  initGlobal: () => Promise<void>;
  init: () => Promise<void>;
  fetchDiscussions: (tab?: DiscussionTab) => Promise<void>;
  fetchMoreDiscussions: () => Promise<void>;
  /** Charge les discussions non lues absentes de la page courante (liste paginée). */
  ensureUnreadDiscussionsLoaded: () => Promise<void>;
  /** Charge UNE discussion absente de la page courante (fil ouvert hors liste). */
  ensureDiscussionLoaded: (id: string) => Promise<void>;
  fetchMessages: (discussionId: string) => Promise<void>;
  fetchOlderMessages: (discussionId: string) => Promise<void>;
  fetchReadReceipts: (discussionId: string) => Promise<void>;
  /** Merge raw Firestore snapshot docs into state (no HTTP round-trip). */
  ingestLiveMessages: (discussionId: string, rawDocs: any[], removedIds?: string[]) => void;
  _mergeMessages: (discussionId: string, list: ChatMessage[]) => void;
  _resolveAttachmentsSoon: (discussionId: string) => void;

  // Actions — sync / triggers
  setActiveTab: (tab: DiscussionTab) => void;
  setSelectedDiscussion: (id: string) => void;
  setSearchQuery: (q: string) => void;
  setViewingDiscussion: (id: string | null) => void;
  setReplyingTo: (messageId: string | null) => void;

  sendMessage: (
    text: string,
    attachment?: ChatAttachment,
    mentions?: string[],
    file?: File,
    opts?: { files?: File[]; folderRefs?: { id: string; name: string }[] },
  ) => Promise<void>;
  sendMessageTo: (
    discussionId: string,
    text: string,
    replyToId?: string | null,
    attachment?: ChatAttachment,
    mentions?: string[],
    file?: File,
    channel?: import("types/messaging").MessageChannel,
    opts?: { files?: File[]; folderRefs?: { id: string; name: string }[] },
  ) => Promise<void>;
  votePoll: (discussionId: string, messageId: string, optionId: string) => Promise<void>;
  closePoll: (discussionId: string, messageId: string) => Promise<void>;
  createPoll: (
    discussionId: string,
    poll: { question: string; options: string[]; multi: boolean; anonymous: boolean },
  ) => Promise<void>;
  /** Re-send an existing message's text + already-uploaded attachments elsewhere. */
  forwardMessage: (targetDiscussionId: string, message: ChatMessage) => Promise<void>;
  updateMessageIn: (discussionId: string, messageId: string, text: string) => Promise<void>;
  deleteMessageIn: (discussionId: string, messageId: string) => Promise<void>;
  toggleReactionIn: (discussionId: string, messageId: string, emoji: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;

  pinMessage: (discussionId: string, messageId: string) => Promise<void>;
  unpinMessage: (discussionId: string, messageId: string) => Promise<void>;

  createDiscussion: (
    name: string,
    memberIds: string[],
    tab: DiscussionTab,
  ) => Promise<string | null>;

  followDiscussion: (discussionId: string) => Promise<void>;
  unfollowDiscussion: (discussionId: string) => Promise<void>;

  sendTyping: (discussionId: string) => void;
  clearTyping: (discussionId: string) => void;

  setPendingScrollToMessageId: (messageId: string | null) => void;
  clearPendingScrollToMessageId: () => void;

  getReadTimeForMessage: (
    receipt: ReadReceipt,
    message: ChatMessage,
    allMessages: ChatMessage[],
  ) => string | null;

  // Derived getters
  getFilteredDiscussions: () => Discussion[];
  getUnreadCount: (tab: DiscussionTab) => number;
  getTotalUnreadCount: () => number;
  getSelectedDiscussion: () => Discussion | undefined;
  getDiscussionMessages: () => ChatMessage[];
  getDiscussionMembers: () => ChatMember[];
  getOnlineCount: () => number;
  getMember: (id: string) => ChatMember | undefined;
  getMessageById: (messageId: string) => ChatMessage | undefined;
  getMatchingMembers: (query: string) => ChatMember[];

}

export const useMessagerieStore = create<MessagerieStore>()(
  persist(
    (set, get) => ({
  currentUserId: "",
  members: [],
  discussions: [],
  messages: {},
  activeTab: "interne",
  selectedDiscussionId: null,
  viewingDiscussionId: null,
  searchQuery: "",
  replyingToMessageId: null,
  unreadCounts: {},
  readReceipts: {},
  pendingScrollToMessageId: null,

  isLoadingDiscussions: false,
  isLoadingMoreDiscussions: false,
  hasMoreDiscussions: false,
  isLoadingMessages: false,
  isLoadingOlderMessages: false,
  hasMoreMessages: {},
  isSending: false,
  uploadProgress: {},
  wsConnected: false,
  typingUsers: {},
  _initialized: false,
  _unsubDiscussions: null,
  _unsubNotifications: null,
  _unsubPresence: null,
  _heartbeatInterval: null,

  // ─── Global init (lightweight — called from MainLayout) ─────
  // Called every time orgMembers changes; only does real work once.
  initGlobal: async () => {
    const firebaseUser = useAuthStore.getState().user;
    if (!firebaseUser) return;
    const orgMembers = useDataStore.getState().orgMembers || [];
    if (orgMembers.length === 0) return;
    const orgData = useDataStore.getState().orgData;
    const orgId = orgData?.id;
    if (!orgId) return;

    // Always keep userId + members fresh
    set({ currentUserId: firebaseUser.uid, members: orgMembers.map(userToChatMember) });

    // Only set up listeners once
    if (!get()._initialized) {
      set({ _initialized: true, wsConnected: true });
      try {
        _lastUnreadCountsFetch = Date.now();
        const counts = await messagerieApi.getUnreadCounts();
        set({ unreadCounts: counts });
      } catch (err) {
        console.error("Failed to fetch unread counts:", err);
      }

      // ── Firestore listener: discussions (new messages + discussion changes) ──
      // Même forme que l'index org_id + members (CONTAINS) + updated_at DESC déjà
      // déployé. updated_at est une chaîne ISO « +00:00 » côté serveur.
      const listenSince = new Date(Date.now() - DISCUSSIONS_LISTEN_WINDOW_MS)
        .toISOString()
        .replace("Z", "+00:00");
      const discQuery = query(
        collection(firebase.db, "discussions"),
        where("org_id", "==", orgId),
        where("members", "array-contains", firebaseUser.uid),
        where("updated_at", ">=", listenSince),
        orderBy("updated_at", "desc"),
      );
      // Track last_message timestamps to detect new messages
      const lastMessageTimes: Record<string, string> = {};
      // Seed with current values so initial snapshot doesn't fire notifications
      get().discussions.forEach((d) => {
        if (d.lastMessageTime) lastMessageTimes[d.id] = d.lastMessageTime;
      });
      let discInitialLoad = true;
      const unsubDisc = onSnapshot(discQuery, (snapshot) => {
        if (discInitialLoad) {
          // Seed timestamps from initial load
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            const lm = data.last_message;
            if (lm?.timestamp) lastMessageTimes[doc.id] = lm.timestamp;
          });
          discInitialLoad = false;
          // Refresh discussions list
          get().fetchDiscussions(get().activeTab);
          return;
        }

        const state = get();
        const userId = state.currentUserId;
        for (const change of snapshot.docChanges()) {
          if (change.type === "added" || change.type === "modified") {
            const data = change.doc.data();
            const discId = change.doc.id;

            // Skip discussions the user is not a member of
            const members: string[] = data.members || [];
            if (userId && !members.includes(userId)) continue;

            const lm = data.last_message;
            const newTime = lm?.timestamp || "";
            const prevTime = lastMessageTimes[discId] || "";

            // Detect new message (timestamp changed)
            if (newTime && newTime !== prevTime) {
              lastMessageTimes[discId] = newTime;
              // Re-read fresh state for viewingDiscussionId (may have changed since snapshot started)
              const freshState = get();
              const isViewing = freshState.viewingDiscussionId === discId;
              const senderIsMe = lm?.sender_id === freshState.currentUserId;

              // Le compteur de non-lus n'est plus incrémenté ici : il est
              // alimenté par le flux user_notifications (voir
              // inAppNotificationsStore), qui est per-utilisateur et scopé aux
              // membres de la discussion côté serveur. Compter aux deux
              // endroits doublerait chaque message.

              // If the user is currently viewing this discussion, refresh messages
              // so the new message appears in the conversation (not just sidebar)
              if (isViewing && !senderIsMe) {
                get().fetchMessages(discId);
              }

              // Update discussion preview in store
              set((s) => ({
                discussions: s.discussions.map((d) =>
                  d.id === discId
                    ? {
                        ...d,
                        lastMessage: lm?.text?.substring(0, 40) || (lm?.attachment_type === "image" ? "Image" : lm?.attachment_type === "audio" ? "Audio" : "Fichier"),
                        lastMessageTime: formatTimestamp(newTime),
                        unread: freshState.viewingDiscussionId !== discId,
                        ...(lm?.sender_id?.startsWith("wa_") ? { whatsapp_last_inbound_at: newTime } : {}),
                      }
                    : d,
                ),
              }));
            }

            // Handle new discussion the user is a member of (not in our list)
            if (change.type === "added" && !state.discussions.some((d) => d.id === discId)) {
              get().fetchDiscussions(get().activeTab);
            }
          }
        }
      }, (err) => {
        console.warn("Discussions Firestore listener error:", err);
      });
      set({ _unsubDiscussions: unsubDisc });

      // ── Re-sync unread counts when returning from background ──
      const handleVisibilityChange = () => {
        if (
          document.visibilityState === "visible" &&
          Date.now() - _lastUnreadCountsFetch >= UNREAD_COUNTS_MIN_INTERVAL_MS
        ) {
          _lastUnreadCountsFetch = Date.now();
          messagerieApi.getUnreadCounts().then((counts) => {
            // Merge: take the max of listener count vs server count per discussion
            const current = get().unreadCounts;
            const merged: Record<string, number> = { ...current };
            for (const [id, count] of Object.entries(counts)) {
              merged[id] = Math.max(merged[id] || 0, count);
            }
            set({ unreadCounts: merged });
          }).catch(() => {});
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      // Store cleanup ref (piggyback on existing unsub pattern)
      const origUnsub = unsubDisc;
      set({ _unsubDiscussions: () => { origUnsub(); document.removeEventListener("visibilitychange", handleVisibilityChange); } });

      // ── Initialize per-user notifications (server-side, replaces org_notifications) ──
      useInAppNotificationsStore.getState().init(firebaseUser.uid);

      // ── Load notification preferences from server ──
      useNotificationPreferencesStore.getState().fetchPreferences();

      // ── Firestore listener: user presence (time-based) ──
      // Cache last_active_time per user so the periodic sweep can re-evaluate
      const lastActiveCache: Record<string, any> = {};

      const usersQuery = query(
        collection(firebase.db, "users"),
        where("org_id", "==", orgId),
      );
      const unsubPresence = onSnapshot(usersQuery, (snapshot) => {
        for (const change of snapshot.docChanges()) {
          if (change.type === "modified" || change.type === "added") {
            const data = change.doc.data();
            const userId = change.doc.id;
            const lastActive = data.activity?.last_active_time;
            lastActiveCache[userId] = lastActive;
            const online = isRecentlyActive(lastActive);

            // Update messagerie store members
            set((s) => ({
              members: s.members.map((m) =>
                m.id === userId ? { ...m, online } : m,
              ),
            }));

            // Update dataStore orgMembers so Team screen also reflects changes
            const dataStore = useDataStore.getState();
            const idx = dataStore.orgMembers.findIndex((m) => m.id === userId);
            if (idx !== -1) {
              const updated = [...dataStore.orgMembers];
              updated[idx] = {
                ...updated[idx],
                activity: { ...updated[idx].activity, ...data.activity },
              };
              useDataStore.setState({ orgMembers: updated });
            }
          }
        }
      }, (err) => {
        console.warn("User presence Firestore listener error:", err);
      });

      // Periodic sweep: re-evaluate online status based on elapsed time
      const presenceSweep = setInterval(() => {
        set((s) => ({
          members: s.members.map((m) => {
            if (m.id === firebaseUser.uid) return m; // skip self
            const cached = lastActiveCache[m.id];
            const online = isRecentlyActive(cached);
            return m.online !== online ? { ...m, online } : m;
          }),
        }));
      }, HEARTBEAT_INTERVAL_MS);

      set({ _unsubPresence: unsubPresence });

      // ── Web presence: heartbeat updates last_active_time periodically ──
      const userRef = doc(firebase.db, "users", firebaseUser.uid);
      const sendHeartbeat = () => {
        updateDoc(userRef, {
          "activity.connected": true,
          "activity.last_active_time": new Date().toISOString(),
        }).catch(() => {});
      };
      // Initial heartbeat
      sendHeartbeat();
      // Periodic heartbeat every 60s
      const heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
      set({ _heartbeatInterval: heartbeatInterval });

      const handleBeforeUnload = () => {
        updateDoc(userRef, {
          "activity.connected": false,
          "activity.last_active_time": new Date(Date.now() - PRESENCE_TIMEOUT_MS).toISOString(),
        }).catch(() => {});
      };
      const handlePresenceVisibility = () => {
        if (document.visibilityState === "visible") {
          sendHeartbeat();
        }
        // When hidden, just stop heartbeating — user will time out naturally
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      document.addEventListener("visibilitychange", handlePresenceVisibility);

      // Wrap presence unsub to also clean up event listeners + disconnect
      const origPresenceUnsub = unsubPresence;
      set({
        _unsubPresence: () => {
          origPresenceUnsub();
          clearInterval(heartbeatInterval);
          clearInterval(presenceSweep);
          set({ _heartbeatInterval: null });
          window.removeEventListener("beforeunload", handleBeforeUnload);
          document.removeEventListener("visibilitychange", handlePresenceVisibility);
          updateDoc(userRef, {
            "activity.connected": false,
            "activity.last_active_time": new Date(Date.now() - PRESENCE_TIMEOUT_MS).toISOString(),
          }).catch(() => {});
        },
      });
    }
  },

  // ─── Full init (called when entering messagerie screen) ─────
  init: async () => {
    const firebaseUser = useAuthStore.getState().user;
    if (!firebaseUser) return;

    // Refresh userId + members
    const orgMembers = useDataStore.getState().orgMembers || [];
    set({ currentUserId: firebaseUser.uid, members: orgMembers.map(userToChatMember) });

    // L'onglet affiché d'abord : un chargement sans onglet ne ramenait que les
    // 25 discussions les plus récentes tous onglets confondus — sur un garage
    // actif ce sont toutes des fils d'ordres, et l'onglet Interne se vidait.
    const tab = get().activeTab;
    await get().fetchDiscussions(tab);
    // L'autre onglet en arrière-plan, pour sa pastille de non-lus. Les
    // chargements fusionnent par onglet : celui-ci ne peut plus écraser l'autre.
    ALL_TABS.filter((t) => t !== tab).forEach((t) => {
      get().fetchDiscussions(t).catch(() => {});
    });
  },

  // ─── Fetch discussions ───────────────────────────────────────
  // Toujours porté par un onglet : le résultat ne remplace que les discussions
  // de cet onglet et laisse les autres en place.
  fetchDiscussions: async (tab?) => {
    const targetTab = tab || get().activeTab;
    const isActiveTab = targetTab === get().activeTab;
    // Deux chargements du même onglet peuvent se croiser (init + listener
    // Firestore) : seule la réponse la plus récente a le droit d'écrire.
    const seq = (_discFetchSeq[targetTab] = (_discFetchSeq[targetTab] || 0) + 1);
    if (isActiveTab) set({ isLoadingDiscussions: true, hasMoreDiscussions: false });
    try {
      const PAGE_SIZE = 25;
      const { items: raw } = await messagerieApi.listDiscussions({ tab: targetTab, limit: PAGE_SIZE });
      if (seq !== _discFetchSeq[targetTab]) return; // réponse périmée
      const { selectedDiscussionId } = get();
      const fetched = raw.map((d) => {
        const disc = normalizeDiscussion(d);
        if (disc.id === selectedDiscussionId) {
          disc.unread = false;
          disc.unread_count = 0;
        }
        return disc;
      });

      const fetchedIds = new Set(fetched.map((d) => d.id));
      const { unreadCounts: liveUnread } = get();
      const existing = get().discussions.filter(
        (d) =>
          !fetchedIds.has(d.id) &&
          // Les fils internes d'un ordre ne sont pas renvoyés par la liste (ils
          // se chargent à la demande) : les garder, sinon leur pastille saute.
          (d.type === "internal" ||
            !tabMatches(d.tab, targetTab) ||
            // Un non-lu (ou le fil ouvert) chargé hors de la première page doit
            // rester : le remplacer par la page affichait la liste complète puis
            // la vidait sous les yeux (onglet Dossiers, 2026-09-28).
            (liveUnread[d.id] || 0) > 0 ||
            d.id === selectedDiscussionId),
      );
      _discLoaded[targetTab] = fetched.length;
      set({
        discussions: [...existing, ...fetched],
        ...(targetTab === get().activeTab ? { hasMoreDiscussions: raw.length === PAGE_SIZE } : {}),
      });

      const currentCounts = { ...get().unreadCounts };
      let countsChanged = false;
      for (const d of fetched) {
        if (!(d.id in currentCounts) && d.unread_count && d.unread_count > 0) {
          currentCounts[d.id] = d.unread_count;
          countsChanged = true;
        }
      }
      if (countsChanged) set({ unreadCounts: currentCounts });
    } catch (err) {
      console.error("Failed to fetch discussions:", err);
    } finally {
      // Un chargement périmé ne coupe pas le loader du chargement en cours.
      if (isActiveTab && seq === _discFetchSeq[targetTab]) set({ isLoadingDiscussions: false });
    }
  },

  fetchMoreDiscussions: async () => {
    const { isLoadingMoreDiscussions, hasMoreDiscussions, activeTab, discussions } = get();
    if (isLoadingMoreDiscussions || !hasMoreDiscussions) return;
    set({ isLoadingMoreDiscussions: true });
    try {
      const PAGE_SIZE = 25;
      const { items: raw } = await messagerieApi.listDiscussions({
        tab: activeTab,
        limit: PAGE_SIZE,
        offset: _discLoaded[activeTab] || 0,
      });
      const { selectedDiscussionId } = get();
      const fetched = raw.map((d) => {
        const disc = normalizeDiscussion(d);
        if (disc.id === selectedDiscussionId) {
          disc.unread = false;
          disc.unread_count = 0;
        }
        return disc;
      });

      const currentCounts = { ...get().unreadCounts };
      let countsChanged = false;
      for (const d of fetched) {
        if (!(d.id in currentCounts) && d.unread_count && d.unread_count > 0) {
          currentCounts[d.id] = d.unread_count;
          countsChanged = true;
        }
      }

      _discLoaded[activeTab] = (_discLoaded[activeTab] || 0) + fetched.length;
      set((s) => {
        // Une discussion non lue hors page a pu être injectée avant d'être paginée.
        const known = new Set(s.discussions.map((d) => d.id));
        return {
          discussions: [...s.discussions, ...fetched.filter((d) => !known.has(d.id))],
          hasMoreDiscussions: raw.length === PAGE_SIZE,
          ...(countsChanged ? { unreadCounts: currentCounts } : {}),
        };
      });
    } catch (err) {
      console.error("Failed to fetch more discussions:", err);
    } finally {
      set({ isLoadingMoreDiscussions: false });
    }
  },

  // La liste des discussions est paginée (25 par onglet) alors que les compteurs
  // de non-lus, eux, couvrent TOUTES les discussions de l'utilisateur. Sans ça,
  // l'onglet « Dossiers » — qui n'affiche que les non-lus — passerait à côté d'un
  // fil non lu resté au-delà de la première page.
  ensureUnreadDiscussionsLoaded: async () => {
    const { discussions, unreadCounts } = get();
    const known = new Set(discussions.map((d) => d.id));
    const missing = Object.entries(unreadCounts)
      .filter(([id, count]) => count > 0 && !known.has(id))
      .map(([id]) => id)
      .slice(0, 50);
    if (missing.length === 0) return;
    try {
      const fetched = await Promise.all(
        missing.map((id) => messagerieApi.getDiscussion(id).catch(() => null)),
      );
      const valid = fetched.filter(Boolean).map((d) => normalizeDiscussion(d as Discussion));
      if (valid.length === 0) return;
      set((s) => {
        const present = new Set(s.discussions.map((d) => d.id));
        const added = valid.filter((d) => !present.has(d.id));
        return added.length ? { discussions: [...s.discussions, ...added] } : {};
      });
    } catch (err) {
      console.error("Failed to load unread discussions:", err);
    }
  },

  // Ouvrir un fil ne garantit pas qu'il soit dans la liste : elle est paginée
  // (25 par onglet) et la modale RDV ouvre un fil quelconque. Tout ce que le
  // panneau lit sur la discussion — numéro WhatsApp, canal, avertissement
  // « ligne fixe », suivi — restait donc vide jusqu'à ce que la liste arrive,
  // une quinzaine de secondes après les messages.
  ensureDiscussionLoaded: async (id) => {
    if (!id || get().discussions.some((d) => d.id === id) || inflightDiscussionLoads.has(id)) return;
    inflightDiscussionLoads.add(id);
    try {
      const fetched = await messagerieApi.getDiscussion(id);
      if (!fetched) return;
      const normalized = normalizeDiscussion(fetched);
      set((s) => (s.discussions.some((d) => d.id === id)
        ? {}
        : { discussions: [...s.discussions, normalized] }));
    } catch (err) {
      console.error("Failed to load discussion:", err);
    } finally {
      inflightDiscussionLoads.delete(id);
    }
  },

  // ─── Fetch messages ──────────────────────────────────────────
  fetchMessages: async (discussionId) => {
    set({ isLoadingMessages: true });
    try {
      const raw = await messagerieApi.listMessages(discussionId, {
        limit: 50,
      });
      const messages = raw.map(normalizeMessage);
      const realIds = new Set(messages.map((m) => m.id));

      set((s) => {
        // Preserve optimistic messages that haven't been confirmed yet
        const optimistic = (s.messages[discussionId] || []).filter(
          (m) => m.id.startsWith("opt_") && !realIds.has(m.id),
        );
        return {
          messages: { ...s.messages, [discussionId]: [...messages, ...optimistic] },
          hasMoreMessages: { ...s.hasMoreMessages, [discussionId]: raw.length === 50 },
        };
      });

      // Mark as read (backend + local)
      if (messages.length > 0) {
        const lastMsg = messages[messages.length - 1];
        messagerieApi.markAsRead(discussionId, lastMsg.id).catch(() => {});
        set((s) => ({
          unreadCounts: { ...s.unreadCounts, [discussionId]: 0 },
          discussions: s.discussions.map((d) =>
            d.id === discussionId ? { ...d, unread: false, unread_count: 0 } : d,
          ),
        }));
      }

      // Fetch read receipts for this discussion
      get().fetchReadReceipts(discussionId);

    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      set({ isLoadingMessages: false });
    }
  },

  // ─── Fetch older messages (scroll up pagination) ──────────
  fetchOlderMessages: async (discussionId) => {
    const { isLoadingOlderMessages, hasMoreMessages, messages } = get();
    if (isLoadingOlderMessages || !hasMoreMessages[discussionId]) return;

    const existing = messages[discussionId] || [];
    if (existing.length === 0) return;

    // Use the oldest message id as cursor
    const oldestMsg = existing[0];
    const beforeId = oldestMsg.id;

    set({ isLoadingOlderMessages: true });
    try {
      const raw = await messagerieApi.listMessages(discussionId, {
        limit: 50,
        before: beforeId,
      });
      const older = raw.map(normalizeMessage);

      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: [...older, ...(s.messages[discussionId] || [])],
        },
        hasMoreMessages: { ...s.hasMoreMessages, [discussionId]: raw.length === 50 },
      }));
    } catch (err) {
      console.error("Failed to fetch older messages:", err);
    } finally {
      set({ isLoadingOlderMessages: false });
    }
  },

  // ─── Read receipts ───────────────────────────────────────────
  fetchReadReceipts: async (discussionId) => {
    try {
      const receipts = await messagerieApi.getReadReceipts(discussionId);
      set((s) => ({ readReceipts: { ...s.readReceipts, [discussionId]: receipts } }));
    } catch {
      // Non-critical — silently ignore
    }
  },

  // Sondages. The server owns the option ids and vote lists, so every action
  // simply replaces the local message with what it returns.
  votePoll: async (discussionId, messageId, optionId) => {
    const updated = await messagerieApi.votePoll(discussionId, messageId, optionId);
    get()._mergeMessages(discussionId, [normalizeMessage(updated)]);
  },

  closePoll: async (discussionId, messageId) => {
    const updated = await messagerieApi.closePoll(discussionId, messageId);
    get()._mergeMessages(discussionId, [normalizeMessage(updated)]);
  },

  createPoll: async (discussionId, poll) => {
    const sent = await messagerieApi.sendMessage(discussionId, { poll });
    get()._mergeMessages(discussionId, [normalizeMessage(sent)]);
  },

  // Forwarding re-sends the *references* to the attachments (same gcs_path), so
  // nothing is re-uploaded. sendMessageTo can't be reused: it only carries a
  // single attachment, which would silently drop the rest of an album.
  forwardMessage: async (targetDiscussionId, message) => {
    if (!targetDiscussionId || !message) return;
    const atts =
      message.attachments && message.attachments.length
        ? message.attachments
        : message.attachment
          ? [message.attachment]
          : [];
    const payload: any = {};
    if (message.text) payload.text = message.text;
    if (atts.length === 1) payload.attachment = atts[0];
    if (atts.length > 1) payload.attachments = atts;
    if (!payload.text && !payload.attachment && !payload.attachments) return;

    const sent = await messagerieApi.sendMessage(targetDiscussionId, payload);
    // Show it immediately if that discussion is already loaded; otherwise the
    // thread's Firestore listener picks it up when it is opened.
    if (get().messages[targetDiscussionId]) {
      get()._mergeMessages(targetDiscussionId, [normalizeMessage(sent)]);
    }
  },

  // ─── Live Firestore ingest ───────────────────────────────────
  // The thread listens to `discussions/{id}/messages` directly. Before, any
  // snapshot triggered a full `fetchMessages()` over HTTP — one round-trip per
  // reaction, edit or read receipt. These merge the snapshot docs straight into
  // state instead, so live updates cost zero network.
  //
  // Raw Firestore docs only carry `gcs_path` (the backend signs `url` at read
  // time), so a message whose attachment URL is still unknown schedules one
  // debounced HTTP fetch purely to resolve it.
  _mergeMessages: (discussionId, list) => {
    set((s) => {
      const existing = s.messages[discussionId] || [];
      const map = new Map(existing.map((m) => [m.id, m]));
      list.forEach((m) => map.set(m.id, { ...(map.get(m.id) || {}), ...m }));
      // "\uffff" comme repli : un message sans created_at vient d'être créé
      // localement, il va donc à la FIN. Avec "" il serait passé avant tout.
      const merged = Array.from(map.values()).sort((a, b) =>
        (a.created_at || "\uffff").localeCompare(b.created_at || "\uffff"),
      );
      return { messages: { ...s.messages, [discussionId]: merged } };
    });
  },

  ingestLiveMessages: (discussionId, rawDocs, removedIds = []) => {
    if (!discussionId || !Array.isArray(rawDocs)) return;
    const existing = get().messages[discussionId] || [];
    const existingById = new Map(existing.map((m) => [m.id, m]));

    let needsResolution = false;
    // Carry an already-signed URL over from the stored copy when the incoming
    // raw doc points at the same file; flag anything still unresolved.
    const fixAtt = (att: any, prevAtt: any) => {
      if (!att || !att.gcs_path) return att;
      const copy = { ...att };
      if (!copy.url && prevAtt && prevAtt.gcs_path === att.gcs_path && prevAtt.url) {
        copy.url = prevAtt.url;
        if (prevAtt.preview_url) copy.preview_url = prevAtt.preview_url;
      }
      if (!copy.url) needsResolution = true;
      return copy;
    };

    const reconciled = rawDocs.map((doc: any) => {
      const prev = existingById.get(doc.id);
      const next: any = normalizeMessage({ ...doc, discussion_id: discussionId });
      if (next.attachment) next.attachment = fixAtt(next.attachment, prev?.attachment);
      if (Array.isArray(next.attachments)) {
        const prevList: any[] = prev?.attachments || [];
        next.attachments = next.attachments.map((a: any, i: number) =>
          fixAtt(a, prevList.find((p) => p && p.gcs_path === a?.gcs_path) || prevList[i]),
        );
      }
      return next as ChatMessage;
    });

    const newIds = reconciled.filter((m) => !existingById.has(m.id)).map((m) => m.id);

    // Drop hard-deleted docs (soft-deletes stay as `deleted` tombstones).
    if (removedIds && removedIds.length) {
      const rm = new Set(removedIds);
      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: (s.messages[discussionId] || []).filter((m) => !rm.has(m.id)),
        },
      }));
    }

    get()._mergeMessages(discussionId, reconciled);

    if (needsResolution) get()._resolveAttachmentsSoon(discussionId);

    // Mark read only on a genuinely new message while the discussion is open —
    // reactions and edits must not move the read pointer.
    if (newIds.length && get().viewingDiscussionId === discussionId) {
      const list = get().messages[discussionId] || [];
      const last = list[list.length - 1];
      if (last) {
        messagerieApi.markAsRead(discussionId, last.id).catch(() => {});
        set((s) => ({
          unreadCounts: { ...s.unreadCounts, [discussionId]: 0 },
          discussions: s.discussions.map((d) =>
            d.id === discussionId ? { ...d, unread: false, unread_count: 0 } : d,
          ),
        }));
      }
    }
  },

  _resolveAttachmentsSoon: (discussionId) => {
    if (_resolveTimers[discussionId]) return; // already scheduled
    _resolveTimers[discussionId] = setTimeout(async () => {
      delete _resolveTimers[discussionId];
      try {
        const raw = await messagerieApi.listMessages(discussionId, { limit: 50 });
        if (Array.isArray(raw)) get()._mergeMessages(discussionId, raw.map(normalizeMessage));
      } catch (err) {
        console.warn("[messagerie] resolve attachments failed:", err);
      }
    }, 250);
  },

  // ─── Tab / selection ─────────────────────────────────────────
  setActiveTab: (tab) => {
    const normalized = normalizeTab(tab);
    set({ activeTab: normalized });
    get().fetchDiscussions(normalized);
  },

  setSelectedDiscussion: (id) => {
    // Only clear unreads for the clicked discussion (client side).
    // Internal sibling unreads are cleared when the user actually switches
    // to the Interne tab (via setViewingDiscussion + fetchMessages).
    const discussions = get().discussions.map((d) =>
      d.id === id ? { ...d, unread: false, unread_count: 0 } : d,
    );
    set({
      selectedDiscussionId: id,
      discussions,
      replyingToMessageId: null,
      unreadCounts: { ...get().unreadCounts, [id]: 0 },
    });

    // Fetch messages if not loaded or refresh
    get().fetchMessages(id);
  },

  setSearchQuery: (q) => set({ searchQuery: q }),
  setViewingDiscussion: (id) => set({ viewingDiscussionId: id }),

  setReplyingTo: (messageId) => set({ replyingToMessageId: messageId }),

  // ─── Send message ────────────────────────────────────────────
  sendMessage: async (text, attachment, mentions, file, opts) => {
    const { selectedDiscussionId, currentUserId, replyingToMessageId } = get();
    if (!selectedDiscussionId) return;

    // Normalise into a list of files (multi-attach), keeping the legacy single
    // `file` param working. `opts.files` takes precedence when provided.
    const folderRefs = opts?.folderRefs;
    let files: File[] = opts?.files && opts.files.length > 0
      ? opts.files
      : file
        ? [file]
        : [];

    if (!text.trim() && !attachment && files.length === 0 && (!folderRefs || folderRefs.length === 0)) return;

    // Compress images before upload (in parallel via compressImages)
    if (files.length > 0) {
      files = await compressImages(files);
    }

    set({ isSending: true });

    const now = new Date();
    const timestamp = now.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Optimistic message with blob URL for instant image preview
    const optimisticId = "opt_" + Date.now();
    const makeOptimisticAttachment = (f: File): ChatAttachment => ({
      type: f.type.startsWith("image/") ? "image" : "file",
      name: f.name,
      mime_type: f.type,
      size: f.size,
      url: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined,
    });

    const optimisticAttachments: ChatAttachment[] = files.map(makeOptimisticAttachment);
    // Legacy single attachment (first file or the provided attachment object).
    let optimisticAttachment: ChatAttachment | undefined = attachment
      ? { ...attachment, url: files[0] && files[0].type.startsWith("image/") ? URL.createObjectURL(files[0]) : attachment.url }
      : optimisticAttachments[0];

    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      senderId: currentUserId,
      sender_id: currentUserId,
      text: text.trim() || undefined,
      attachment: optimisticAttachment,
      attachments: optimisticAttachments.length > 0 ? optimisticAttachments : undefined,
      folder_refs: folderRefs && folderRefs.length > 0 ? folderRefs : undefined,
      reactions: [],
      timestamp,
      // Les messages sont ordonnés sur created_at. Sans lui, un message
      // optimiste se retrouvait trié avant TOUS les autres (chaîne vide) et
      // disparaissait tout en haut de la conversation pour son auteur.
      created_at: new Date().toISOString(),
      replyToId: replyingToMessageId || undefined,
      reply_to_id: replyingToMessageId || undefined,
      mentions: mentions && mentions.length > 0 ? mentions : undefined,
    };

    // Optimistic insert (+ set uploadProgress for file uploads)
    set((s) => ({
      messages: {
        ...s.messages,
        [selectedDiscussionId]: [
          ...(s.messages[selectedDiscussionId] || []),
          optimisticMsg,
        ],
      },
      replyingToMessageId: null,
      uploadProgress: files.length > 0 ? { ...s.uploadProgress, [optimisticId]: 0 } : s.uploadProgress,
    }));

    // Update discussion preview optimistically
    const previewAttachment = attachment || optimisticAttachments[0];
    const isAudio = previewAttachment && (previewAttachment.mime_type || previewAttachment.mimeType || "").startsWith("audio/");
    const preview = previewAttachment
      ? previewAttachment.type === "image"
        ? "Image"
        : isAudio
          ? "Audio"
          : `${previewAttachment.name || "Fichier"}`
      : text.trim().substring(0, 40) + (text.trim().length > 40 ? ".." : "");

    set((s) => ({
      discussions: s.discussions.map((d) =>
        d.id === selectedDiscussionId
          ? { ...d, lastMessage: preview, lastMessageTime: timestamp }
          : d,
      ),
    }));

    try {
      // Upload files if provided. Aggregate progress across all files.
      let finalAttachment = attachment;
      let finalAttachments: ChatAttachment[] | undefined;
      if (files.length > 0) {
        const progressByIdx: number[] = new Array(files.length).fill(0);
        const reportAggregate = () => {
          const total = progressByIdx.reduce((a, b) => a + b, 0) / files.length;
          set((s) => ({ uploadProgress: { ...s.uploadProgress, [optimisticId]: Math.round(total) } }));
        };
        finalAttachments = await Promise.all(
          files.map(async (f, idx) => {
            const uploaded = await messagerieApi.uploadAttachment(
              selectedDiscussionId,
              f,
              (progress) => { progressByIdx[idx] = progress; reportAggregate(); },
            );
            return {
              type: f.type.startsWith("image/") ? "image" : "file",
              name: uploaded.name,
              gcs_path: uploaded.gcs_path,
              mime_type: uploaded.mime_type,
              size: uploaded.size,
              preview_gcs_path: uploaded.preview_gcs_path,
              pages: uploaded.pages,
            } as ChatAttachment;
          }),
        );
        finalAttachment = finalAttachments[0];
      }

      const payload: {
        text?: string;
        attachment?: ChatAttachment;
        attachments?: ChatAttachment[];
        folder_refs?: { id: string; name: string }[];
        reply_to_id?: string;
        mentions?: string[];
      } = {};
      if (text.trim()) payload.text = text.trim();
      if (finalAttachment) payload.attachment = finalAttachment;
      if (finalAttachments && finalAttachments.length > 0) payload.attachments = finalAttachments;
      if (folderRefs && folderRefs.length > 0) payload.folder_refs = folderRefs;
      if (replyingToMessageId) payload.reply_to_id = replyingToMessageId;
      if (mentions && mentions.length > 0) payload.mentions = mentions;

      const sent = await messagerieApi.sendMessage(
        selectedDiscussionId,
        payload,
      );

      // Replace optimistic message with real one, then deduplicate
      // (WS may have already appended the real message before the API response)
      const real = normalizeMessage(sent);
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [selectedDiscussionId]: (s.messages[selectedDiscussionId] || [])
              .map((m) => (m.id === optimisticId ? real : m))
              .filter((m, idx, arr) => idx === arr.findIndex((x) => x.id === m.id)),
          },
          uploadProgress: restProgress,
        };
      });
    } catch (err) {
      console.error("Failed to send message:", err);
      // Remove optimistic message on failure
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [selectedDiscussionId]: (
              s.messages[selectedDiscussionId] || []
            ).filter((m) => m.id !== optimisticId),
          },
          uploadProgress: restProgress,
        };
      });
    } finally {
      set({ isSending: false });
    }
  },

  // ─── Send message to a specific discussion (without changing global selection) ──
  sendMessageTo: async (discussionId, text, replyToId, attachment, mentions, file, channel, opts) => {
    const { currentUserId } = get();
    if (!discussionId) return;

    const folderRefs = opts?.folderRefs;
    let files: File[] = opts?.files && opts.files.length > 0
      ? opts.files
      : file
        ? [file]
        : [];

    if (!text.trim() && !attachment && files.length === 0 && (!folderRefs || folderRefs.length === 0)) return;

    // Compress images before upload (in parallel via compressImages)
    if (files.length > 0) {
      files = await compressImages(files);
    }

    set({ isSending: true });

    const now = new Date();
    const timestamp = now.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const optimisticId = "opt_" + Date.now();
    const makeOptimisticAttachment = (f: File): ChatAttachment => ({
      type: f.type.startsWith("image/") ? "image" : "file",
      name: f.name,
      mime_type: f.type,
      size: f.size,
      url: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined,
    });

    const optimisticAttachments: ChatAttachment[] = files.map(makeOptimisticAttachment);
    let optimisticAttachment: ChatAttachment | undefined = attachment
      ? { ...attachment, url: files[0] && files[0].type.startsWith("image/") ? URL.createObjectURL(files[0]) : attachment.url }
      : optimisticAttachments[0];

    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      senderId: currentUserId,
      sender_id: currentUserId,
      text: text.trim() || undefined,
      attachment: optimisticAttachment,
      attachments: optimisticAttachments.length > 0 ? optimisticAttachments : undefined,
      folder_refs: folderRefs && folderRefs.length > 0 ? folderRefs : undefined,
      reactions: [],
      timestamp,
      // Voir sendMessage : sans created_at, l'ordre de tri place le message
      // optimiste avant tous les autres.
      created_at: new Date().toISOString(),
      replyToId: replyToId || undefined,
      reply_to_id: replyToId || undefined,
      mentions: mentions && mentions.length > 0 ? mentions : undefined,
    };

    set((s) => ({
      messages: {
        ...s.messages,
        [discussionId]: [
          ...(s.messages[discussionId] || []),
          optimisticMsg,
        ],
      },
      uploadProgress: files.length > 0 ? { ...s.uploadProgress, [optimisticId]: 0 } : s.uploadProgress,
    }));

    try {
      let finalAttachment = attachment;
      let finalAttachments: ChatAttachment[] | undefined;
      if (files.length > 0) {
        const progressByIdx: number[] = new Array(files.length).fill(0);
        const reportAggregate = () => {
          const total = progressByIdx.reduce((a, b) => a + b, 0) / files.length;
          set((s) => ({ uploadProgress: { ...s.uploadProgress, [optimisticId]: Math.round(total) } }));
        };
        finalAttachments = await Promise.all(
          files.map(async (f, idx) => {
            const uploaded = await messagerieApi.uploadAttachment(
              discussionId,
              f,
              (progress) => { progressByIdx[idx] = progress; reportAggregate(); },
            );
            return {
              type: f.type.startsWith("image/") ? "image" : "file",
              name: uploaded.name,
              gcs_path: uploaded.gcs_path,
              mime_type: uploaded.mime_type,
              size: uploaded.size,
              preview_gcs_path: uploaded.preview_gcs_path,
              pages: uploaded.pages,
            } as ChatAttachment;
          }),
        );
        finalAttachment = finalAttachments[0];
      }

      const payload: {
        text?: string;
        attachment?: ChatAttachment;
        attachments?: ChatAttachment[];
        folder_refs?: { id: string; name: string }[];
        reply_to_id?: string;
        mentions?: string[];
        channel?: import("types/messaging").MessageChannel;
      } = {};
      if (text.trim()) payload.text = text.trim();
      if (finalAttachment) payload.attachment = finalAttachment;
      if (finalAttachments && finalAttachments.length > 0) payload.attachments = finalAttachments;
      if (folderRefs && folderRefs.length > 0) payload.folder_refs = folderRefs;
      if (replyToId) payload.reply_to_id = replyToId;
      if (mentions && mentions.length > 0) payload.mentions = mentions;
      if (channel) payload.channel = channel;

      const sent = await messagerieApi.sendMessage(discussionId, payload);
      const real = normalizeMessage(sent);
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [discussionId]: (s.messages[discussionId] || [])
              .map((m) => (m.id === optimisticId ? real : m))
              .filter((m, idx, arr) => idx === arr.findIndex((x) => x.id === m.id)),
          },
          uploadProgress: restProgress,
        };
      });
    } catch (err) {
      console.error("Failed to send message:", err);
      // Le message tapé disparaît du fil : dire pourquoi (ex. « Aucun e-mail pour ce
      // client ») au lieu de le laisser s'évanouir en silence.
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      showNotification({
        type: "error",
        message: typeof detail === "string" && detail ? detail : "Le message n'a pas pu être envoyé",
      });
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [discussionId]: (s.messages[discussionId] || []).filter(
              (m) => m.id !== optimisticId,
            ),
          },
          uploadProgress: restProgress,
        };
      });
    } finally {
      set({ isSending: false });
    }
  },

  // ─── Update message text ─────────────────────────────────────
  updateMessageIn: async (discussionId, messageId, text) => {
    // Optimistic update
    set((s) => ({
      messages: {
        ...s.messages,
        [discussionId]: (s.messages[discussionId] || []).map((m) =>
          m.id === messageId ? { ...m, text, edited: true } : m,
        ),
      },
    }));
    try {
      const sent = await messagerieApi.updateMessage(discussionId, messageId, text);
      const real = normalizeMessage(sent);
      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: (s.messages[discussionId] || []).map((m) =>
            m.id === messageId ? real : m,
          ),
        },
      }));
    } catch (err) {
      console.error("Failed to update message:", err);
    }
  },

  // ─── Delete message in a specific discussion ─────────────────
  deleteMessageIn: async (discussionId, messageId) => {
    const { currentUserId } = get();
    const msgs = get().messages[discussionId];
    if (!msgs) return;

    const msg = msgs.find((m) => m.id === messageId);
    if (!msg) return;
    const senderId = msg.sender_id || msg.senderId;
    if (senderId !== currentUserId) return;

    set((s) => {
      const updatedMsgs = (s.messages[discussionId] || []).map((m) =>
        m.id === messageId
          ? { ...m, deleted: true, text: undefined, attachment: undefined, attachments: undefined, reactions: [] }
          : m,
      );

      // If the deleted message was the discussion's last, recompute the local
      // sidebar preview from the remaining non-deleted messages.
      const wasLast = msgs[msgs.length - 1]?.id === messageId;
      const discussions = s.discussions.map((d) => {
        if (d.id !== discussionId) return d;
        let next = d;
        // Drop from local pinned_messages if present.
        if (d.pinned_messages?.some((p) => p.message_id === messageId)) {
          next = { ...next, pinned_messages: d.pinned_messages.filter((p) => p.message_id !== messageId) };
        }
        if (wasLast) {
          const lastVisible = [...updatedMsgs].reverse().find((m) => !m.deleted);
          const { text, time } = previewFromMessage(lastVisible);
          next = { ...next, lastMessage: text, lastMessageTime: time };
        }
        return next;
      });

      return {
        messages: { ...s.messages, [discussionId]: updatedMsgs },
        discussions,
      };
    });

    try {
      await messagerieApi.deleteMessage(discussionId, messageId);
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  },

  // ─── Toggle reaction in a specific discussion ────────────────
  toggleReactionIn: async (discussionId, messageId, emoji) => {
    const { currentUserId } = get();
    if (!discussionId) return;

    set((s) => {
      const msgs = s.messages[discussionId] || [];
      return {
        messages: {
          ...s.messages,
          [discussionId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions.findIndex(
              (r) =>
                r.emoji === emoji &&
                (r.user_id === currentUserId || r.userId === currentUserId),
            );
            const reactions =
              existing >= 0
                ? m.reactions.filter((_, i) => i !== existing)
                : [
                    ...m.reactions,
                    { emoji, userId: currentUserId, user_id: currentUserId },
                  ];
            return { ...m, reactions };
          }),
        },
      };
    });

    try {
      await messagerieApi.toggleReaction(discussionId, messageId, emoji);
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  },

  // ─── Delete message ──────────────────────────────────────────
  deleteMessage: async (messageId) => {
    const { selectedDiscussionId, currentUserId } = get();
    if (!selectedDiscussionId) return;

    const msgs = get().messages[selectedDiscussionId];
    if (!msgs) return;

    const msg = msgs.find((m) => m.id === messageId);
    if (!msg) return;
    const senderId = msg.sender_id || msg.senderId;
    if (senderId !== currentUserId) return;

    // Optimistic delete
    set((s) => {
      const updatedMsgs = (s.messages[selectedDiscussionId] || []).map((m) =>
        m.id === messageId
          ? {
              ...m,
              deleted: true,
              text: undefined,
              attachment: undefined,
              attachments: undefined,
              reactions: [],
            }
          : m,
      );

      const wasLast = msgs[msgs.length - 1]?.id === messageId;
      const discussions = s.discussions.map((d) => {
        if (d.id !== selectedDiscussionId) return d;
        let next = d;
        if (d.pinned_messages?.some((p) => p.message_id === messageId)) {
          next = { ...next, pinned_messages: d.pinned_messages.filter((p) => p.message_id !== messageId) };
        }
        if (wasLast) {
          const lastVisible = [...updatedMsgs].reverse().find((m) => !m.deleted);
          const { text, time } = previewFromMessage(lastVisible);
          next = { ...next, lastMessage: text, lastMessageTime: time };
        }
        return next;
      });

      return {
        messages: { ...s.messages, [selectedDiscussionId]: updatedMsgs },
        discussions,
      };
    });

    try {
      await messagerieApi.deleteMessage(selectedDiscussionId, messageId);
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  },

  // ─── Toggle reaction ─────────────────────────────────────────
  toggleReaction: async (messageId, emoji) => {
    const { selectedDiscussionId, currentUserId } = get();
    if (!selectedDiscussionId) return;

    // Optimistic toggle
    set((s) => {
      const msgs = s.messages[selectedDiscussionId] || [];
      return {
        messages: {
          ...s.messages,
          [selectedDiscussionId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions.findIndex(
              (r) =>
                r.emoji === emoji &&
                (r.user_id === currentUserId || r.userId === currentUserId),
            );
            const reactions =
              existing >= 0
                ? m.reactions.filter((_, i) => i !== existing)
                : [
                    ...m.reactions,
                    { emoji, userId: currentUserId, user_id: currentUserId },
                  ];
            return { ...m, reactions };
          }),
        },
      };
    });

    try {
      await messagerieApi.toggleReaction(
        selectedDiscussionId,
        messageId,
        emoji,
      );
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  },

  // ─── Pin / Unpin message ─────────────────────────────────────
  // Backend returns the updated discussion; merge its pinned_messages into the
  // store discussion so the pinned banner updates instantly.
  pinMessage: async (discussionId, messageId) => {
    try {
      const updated = await messagerieApi.pinMessage(discussionId, messageId);
      set((s) => ({
        discussions: s.discussions.map((d) =>
          d.id === discussionId ? { ...d, pinned_messages: updated.pinned_messages || [] } : d,
        ),
      }));
    } catch (err) {
      console.error("Failed to pin message:", err);
    }
  },

  unpinMessage: async (discussionId, messageId) => {
    try {
      const updated = await messagerieApi.unpinMessage(discussionId, messageId);
      set((s) => ({
        discussions: s.discussions.map((d) =>
          d.id === discussionId ? { ...d, pinned_messages: updated.pinned_messages || [] } : d,
        ),
      }));
    } catch (err) {
      console.error("Failed to unpin message:", err);
    }
  },

  // ─── Create discussion ───────────────────────────────────────
  createDiscussion: async (name, memberIds, tab) => {
    try {
      const raw = await messagerieApi.createDiscussion({
        tab,
        name,
        member_ids: memberIds,
      });
      const disc = normalizeDiscussion(raw);

      set((s) => ({
        discussions: [disc, ...s.discussions],
        messages: { ...s.messages, [disc.id]: [] },
        selectedDiscussionId: disc.id,
        // Créée en "generales" ou en "privees", elle s'affiche dans l'onglet qui
        // réunit les deux.
        activeTab: normalizeTab(tab),
      }));
      return disc.id;
    } catch (err) {
      console.error("Failed to create discussion:", err);
      return null;
    }
  },

  // ─── Follow / Unfollow ──────────────────────────────────────
  followDiscussion: async (discussionId: string) => {
    await messagerieApi.followDiscussion(discussionId);
    const userId = get().currentUserId;
    set((s) => ({
      discussions: s.discussions.map((d) =>
        d.id === discussionId && !d.members?.includes(userId)
          ? { ...d, members: [...(d.members || []), userId] }
          : d,
      ),
    }));
    // Refetch ordres tab so the discussion appears in the list
    get().fetchDiscussions("ordres").catch(() => {});
  },

  unfollowDiscussion: async (discussionId: string) => {
    await messagerieApi.unfollowDiscussion(discussionId);
    // Remove the discussion from the list and clear its unread count immediately
    set((s) => {
      const { [discussionId]: _, ...restCounts } = s.unreadCounts;
      return {
        discussions: s.discussions.filter((d) => d.id !== discussionId),
        unreadCounts: restCounts,
      };
    });
    // Refetch to sync with backend
    get().fetchDiscussions(get().activeTab).catch(() => {});
  },

  // ─── Typing indicator ───────────────────────────────────────
  sendTyping: (discussionId: string) => {
    const userId = get().currentUserId;
    if (!userId || !discussionId) return;
    const ref = doc(firebase.db, "discussions", discussionId, "typing", userId);
    setDoc(ref, { timestamp: serverTimestamp() }).catch(() => {});
  },

  clearTyping: (discussionId: string) => {
    const userId = get().currentUserId;
    if (!userId || !discussionId) return;
    const ref = doc(firebase.db, "discussions", discussionId, "typing", userId);
    deleteDoc(ref).catch(() => {});
  },

  // ─── Jump-to-message (transient state for the UI) ─────────────
  setPendingScrollToMessageId: (messageId) => set({ pendingScrollToMessageId: messageId }),
  clearPendingScrollToMessageId: () => set({ pendingScrollToMessageId: null }),

  // ─── Checkpoint-accurate read time ───────────────────────────
  // Resolve when `receipt`'s user FIRST saw `message`. Walk checkpoints[], pick
  // the earliest (by time) whose message index is at-or-after the target's index;
  // fall back to last_read_at for legacy receipts with no checkpoints.
  // Mirrors hexagone readTimeForMessage (MessagerieThread.js ~370-380).
  getReadTimeForMessage: (receipt, message, allMessages) => {
    if (!receipt) return null;
    const msgIdx = (allMessages || []).findIndex((m) => m.id === message.id);
    if (msgIdx < 0) return receipt.last_read_at || null;
    let best: string | null = null;
    for (const cp of receipt.checkpoints || []) {
      const ci = (allMessages || []).findIndex((m) => m.id === cp.message_id);
      if (ci >= 0 && ci >= msgIdx && (best === null || (cp.read_at || "") < best)) {
        best = cp.read_at || "";
      }
    }
    return best !== null ? best : receipt.last_read_at || null;
  },

  // ─── Derived getters ────────────────────────────────────────
  getFilteredDiscussions: () => {
    const { discussions, activeTab, searchQuery, selectedDiscussionId } = get();
    const visible = discussions.filter((d) => {
      // Hide internal entity discussions from sidebar — accessed via ChatPanel tabs
      if (d.type === "internal") return false;
      if (!tabMatches(d.tab, activeTab)) return false;
      // Un fil sans aucun message (créé par la synchro, jamais utilisé) n'a rien
      // à montrer : on le masque. Sauf le fil ouvert — une discussion qu'on vient
      // de créer est vide jusqu'au premier message et doit rester sous les yeux.
      if (!lastMessageAt(d) && d.id !== selectedDiscussionId) return false;
      if (searchQuery) {
        if (
          !includesNormalized(d.name, searchQuery) &&
          !includesNormalized(d.lastMessage || "", searchQuery)
        )
          return false;
      }
      return true;
    });
    // Ordre strict par dernier message. Rien d'autre n'entre dans le tri :
    // remonter les non-lus ou la conversation ouverte faisait sauter les lignes
    // sous la souris à chaque clic — la pastille suffit à distinguer un non-lu.
    return visible.sort(compareByLastMessage);
  },

  getUnreadCount: (tab) => {
    const discussions = get().discussions;
    const unreadCounts = get().unreadCounts;
    return discussions.filter((d) => {
      if (d.type === "internal") return false;
      if (!tabMatches(d.tab, tab)) return false;
      // Use only unreadCounts (live listener) as source of truth
      return discussionHasUnread(d, discussions, unreadCounts);
    }).length;
  },

  getTotalUnreadCount: () => {
    return Object.values(get().unreadCounts).reduce((sum, c) => sum + c, 0);
  },

  getSelectedDiscussion: () => {
    const { discussions, selectedDiscussionId } = get();
    return discussions.find((d) => d.id === selectedDiscussionId);
  },

  getDiscussionMessages: () => {
    const { messages, selectedDiscussionId } = get();
    if (!selectedDiscussionId) return [];
    return messages[selectedDiscussionId] || [];
  },

  getDiscussionMembers: () => {
    const disc = get().getSelectedDiscussion();
    if (!disc) return [];
    return get().members.filter((m) => disc.members.includes(m.id));
  },

  getOnlineCount: () => {
    const members = get().getDiscussionMembers();
    return members.filter((m) => m.online).length;
  },

  getMember: (id) => {
    return get().members.find((m) => m.id === id);
  },

  getMessageById: (messageId) => {
    const { messages, selectedDiscussionId } = get();
    if (!selectedDiscussionId) return undefined;
    const msgs = messages[selectedDiscussionId];
    return msgs?.find((m) => m.id === messageId);
  },

  getMatchingMembers: (query) => {
    const members = get().getDiscussionMembers();
    const currentUserId = get().currentUserId;
    const others = members.filter((m) => m.id !== currentUserId);
    if (!query) return others;
    return others.filter((m) => matchesNamePrefix(m.name, query));
  },
    }),
    {
      name: "messagerie-prefs",
      partialize: (state) => ({
        activeTab: state.activeTab,
      }),
      // Un onglet mémorisé avant la fusion ("generales" / "privees") n'existe plus
      // dans la barre : on le ramène sur l'onglet qui les réunit.
      merge: (persisted, current) => {
        const saved = (persisted as { activeTab?: DiscussionTab } | undefined)?.activeTab;
        return { ...current, ...(persisted as object), activeTab: normalizeTab(saved || current.activeTab) };
      },
    },
  ),
);
